/**
 * Sample "photos" for the demo store: small SVGs that look like a close-up of
 * painted aluminium with a mark matching the damage category. Generated, so
 * the prototype makes no external request and ships no binary assets.
 */
import type { DamageCategory } from '@fsv/core';
import { svgDataUrl } from '../drawings/silhouettes';

function rng(seed: number): () => number {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

const PAINTS = [
  { base: '#F4F5F2', shade: '#D9DCD6', stripe: '#1F3A93' },
  { base: '#F2F2EE', shade: '#D6D8D2', stripe: '#B3261E' },
  { base: '#EEF1F4', shade: '#CDD3DA', stripe: '#10243A' },
];

function mark(category: DamageCategory, r: () => number): string {
  const cx = 360 + r() * 480;
  const cy = 260 + r() * 300;
  switch (category) {
    case 'kratzer': {
      let d = '';
      for (let i = 0; i < 4; i++) {
        const y = cy + (r() - 0.5) * 50;
        d += `M${cx - 220 + r() * 40} ${y} Q${cx} ${y - 30 + r() * 60} ${cx + 200 + r() * 40} ${y + (r() - 0.5) * 40} `;
      }
      return `<path d="${d}" stroke="#6E737A" stroke-width="${2 + r() * 2}" fill="none" stroke-linecap="round" opacity="0.85"/>`;
    }
    case 'beule':
      return (
        `<ellipse cx="${cx}" cy="${cy}" rx="170" ry="110" fill="url(#dent)"/>` +
        `<ellipse cx="${cx - 30}" cy="${cy - 25}" rx="90" ry="45" fill="#FFFFFF" opacity="0.35"/>`
      );
    case 'lack': {
      let d = `M${cx} ${cy - 90}`;
      for (let a = 0; a < Math.PI * 2; a += Math.PI / 9) {
        const rad = 70 + r() * 60;
        d += ` L${cx + Math.cos(a) * rad * 1.4} ${cy + Math.sin(a) * rad}`;
      }
      return `<path d="${d} Z" fill="#B7BDC4" stroke="#8C939B" stroke-width="3"/><path d="${d} Z" fill="url(#alu)" opacity="0.7" transform="translate(${cx * 0.08} ${cy * 0.08}) scale(0.92)"/>`;
    }
    case 'riss': {
      let d = `M${cx - 200} ${cy}`;
      for (let i = 1; i <= 10; i++) d += ` L${cx - 200 + i * 40} ${cy + (r() - 0.5) * 60}`;
      return `<path d="${d}" stroke="#2B2F33" stroke-width="3" fill="none"/><circle cx="${cx - 200}" cy="${cy}" r="9" fill="none" stroke="#2B2F33" stroke-width="3"/>`;
    }
    case 'sonstiges':
      return `<rect x="${cx - 120}" y="${cy - 60}" width="240" height="120" rx="20" fill="#8A6A3B" opacity="0.55"/><circle cx="${cx + 40}" cy="${cy + 10}" r="34" fill="#5B4423" opacity="0.6"/>`;
  }
}

export function placeholderPhoto(category: DamageCategory, seed: number, overview = false): string {
  const r = rng(seed * 7919 + 13);
  const paint = PAINTS[seed % PAINTS.length] ?? PAINTS[0]!;
  const stripeY = 120 + r() * 520;
  const rivets = Array.from({ length: 14 }, (_, i) => `<circle cx="${60 + i * 80}" cy="${stripeY - 40}" r="5" fill="${paint.shade}"/>`).join('');
  const content = overview
    ? `<rect x="80" y="${stripeY - 160}" width="1040" height="420" rx="200" fill="${paint.base}" stroke="${paint.shade}" stroke-width="6"/>` +
      `<rect x="80" y="${stripeY}" width="1040" height="40" fill="${paint.stripe}"/>` +
      `<circle cx="${400 + r() * 400}" cy="${stripeY - 60 + r() * 120}" r="46" fill="none" stroke="#E8602C" stroke-width="8"/>`
    : `<rect x="0" y="${stripeY}" width="1200" height="${36 + r() * 30}" fill="${paint.stripe}"/>${rivets}${mark(category, r)}`;
  const bg = overview ? '#8FA3B8' : paint.base;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 900" width="1200" height="900">` +
    `<defs><radialGradient id="dent"><stop offset="0" stop-color="#9BA1A8"/><stop offset="1" stop-color="${paint.base}" stop-opacity="0"/></radialGradient>` +
    `<linearGradient id="alu" x1="0" x2="1"><stop offset="0" stop-color="#D5D9DE"/><stop offset="1" stop-color="#9DA4AC"/></linearGradient>` +
    `<linearGradient id="light" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#FFFFFF" stop-opacity="0.5"/><stop offset="1" stop-color="#000000" stop-opacity="0.12"/></linearGradient></defs>` +
    `<rect width="1200" height="900" fill="${bg}"/>${content}<rect width="1200" height="900" fill="url(#light)"/>` +
    `<text x="1180" y="878" text-anchor="end" font-family="sans-serif" font-size="30" fill="#10243A" opacity="0.4">Beispielfoto</text></svg>`;
  return svgDataUrl(svg);
}
